import { FC, useState } from 'react';
import classNames from 'classnames';

import { CurrencyInput } from 'components/design-system/CurrencyInput';
import { inputStyle } from 'components/design-system/Input';
import { Slideout, SlideoutCommonProps, SubmitButtonWithArrow } from 'components/design-system/Slideout';
import { Text } from 'components/design-system/Text';
import { PayoutBottomSheet } from 'components/offers/PayoutBottomSheet';
import { minimumPriceCents } from 'consts/price';
import { useModal } from 'context/modal/ModalContext';
import { clientApiPost } from 'lib/api_helper';
import styles from 'styles/FinalOffer.module.scss';
import { Offer, SellerLevel, SellerProfile } from 'types';
import { formatPrice, getListingBreakdownPrice } from 'utils/text-helpers';

import { FinalOfferSubmittedSlideout } from './FinalOfferSubmittedSlideout';

export const FinalOfferPriceSlideoutTestIds = {
  container: 'final-offer-price-slideout',
  earnings: 'final-offer-price-slideout-earnings',
  submit: 'final-offer-price-slideout-submit'
};

export type FinalOfferPriceSlideoutProps = SlideoutCommonProps & {
  offer: Offer;
  sellerProfile: SellerProfile | null;
  sellerLevels?: SellerLevel[];
};

// TODO create and reuse a new component for price slideout
export const FinalOfferPriceSlideout: FC<FinalOfferPriceSlideoutProps> = ({
  offer,
  sellerProfile,
  sellerLevels,
  ...props
}) => {
  const maximumPrice = offer.dealTrigger?.dealPriceCents || minimumPriceCents;
  const { closeModal, openModal } = useModal();
  const [warningText, setWarningText] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPayoutBottomSheetOpen, setIsPayoutBottomSheetOpen] = useState(false);
  const [price, setPrice] = useState(maximumPrice);
  const stringPrice = price ? String(price / 100) : undefined;

  const { earnings, commissionCents, sellerFeeCents, minimumTotalFee } = getListingBreakdownPrice(
    { priceCents: price || 0 },
    sellerProfile,
    sellerLevels
  );
  const openFinalOfferSubmittedSlideout = () => {
    openModal(<FinalOfferSubmittedSlideout closeModal={closeModal} offer={offer} finalPrice={price} />);
  };

  const onConfirm = async () => {
    setIsLoading(true);
    await clientApiPost(`/api/finalOffer/triggerDeal?dealTriggerId=${offer.dealTrigger?.id}`, {
      priceCents: price
    });
    setIsLoading(false);
    openFinalOfferSubmittedSlideout();
  };

  const placeholder = formatPrice(offer.priceCents).slice(1);

  const onBlur = () => {
    if (!price) {
      return;
    }

    if (price < minimumPriceCents) {
      setWarningText('Minimum price is $1000');
    } else if (price > maximumPrice) {
      setWarningText('Price cannot be higher than the latest counter');
    }
  };

  return (
    <Slideout
      bodyClassName='d-flex flex-1 flex-column'
      title='Set your Final Offer price for 24 hours'
      data-testid={FinalOfferPriceSlideoutTestIds.container}
      submit={{
        testId: FinalOfferPriceSlideoutTestIds.submit,
        textClassname: 'w-100',
        title: <SubmitButtonWithArrow text='Confirm boost' />,
        onClick: onConfirm,
        loading: isLoading,
        disabled: isLoading || !price || price < minimumPriceCents || price > maximumPrice
      }}
      {...props}
    >
      <div className='d-flex flex-1 flex-column justify-content-between riforma-regular fs-16px align-items-center'>
        <div className='mt-4 mb-2 fs-14px text-center'>
          We start at your last counter, but please set the watch as competitively as possible
        </div>
        <div>
          <div className='d-flex justify-content-center w-100 text-primary mb-4'>
            <Text style={inputStyle}>$ </Text>
            <CurrencyInput
              autoFocus
              isResponsive
              onFocus={() => setWarningText(null)}
              onBlur={onBlur}
              className='text-center text-primary'
              value={stringPrice}
              placeholder={placeholder}
              onValueChange={value => setPrice(Number(value || 0) * 100)}
              style={{ border: 'none', background: 'none', minWidth: '200px', ...inputStyle }}
            />
          </div>
          <div
            style={{ minHeight: 40 }}
            className={classNames('text-danger fs-12px text-center', { 'opacity-0': !warningText })}
          >
            {warningText}
          </div>
        </div>
        <div
          data-testid={FinalOfferPriceSlideoutTestIds.earnings}
          role='button'
          onClick={() => setIsPayoutBottomSheetOpen(true)}
          className={classNames(styles.price, 'mt-5')}
        >
          <span className='pe-1 opacity-60'>You’ll make</span>
          <span className='text-decoration-underline'>{formatPrice(earnings)}</span>
        </div>
        {isPayoutBottomSheetOpen && (
          <PayoutBottomSheet
            onClose={() => setIsPayoutBottomSheetOpen(false)}
            earnings={earnings}
            commission={commissionCents}
            sellingPrice={price}
            sellerFee={sellerFeeCents}
            minimalFee={minimumTotalFee}
          />
        )}
      </div>
    </Slideout>
  );
};
